import { useMemo } from "react";

import clsx from "clsx";
import { round } from "lodash";

import { LoadingSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { getFractionChanged } from "@features/mmm/utils/calculators";
import {
  formatCurrencyToDollarAmount,
  formatPotentiallyInfiniteNumber,
  getCurrencySymbol,
} from "@features/mmm/utils/formatters";
import {
  calculateCac,
  calculateRoas,
  type BudgetMixDatumSummaryData,
  type FullBudgetMixResponse,
} from "@north-beam/nb-common";
import { formatCurrency, formatMetric } from "@north-beam/nb-common/formatting";

import { KpiType, type Kpi } from "../../explore-budgets/client-setting/types";
import { type BudgetMixInput } from "../../explore-budgets/scenario-modeling/types";

export type ForecastSpendData = {
  date: string;
  data: Record<string, number>;
};
export type ForecastComparisonWidgetProps = {
  adoptedBudgetMix: FullBudgetMixResponse | null;
  proposedBudgetMix: BudgetMixInput | null;
  selectedKpi: Kpi;
  isLoading: boolean;
  height: number;
};

enum SupplimentalKpiType {
  ROAS = "roas",
  CAC = "cac",
}

enum ChangeDirection {
  UP = "up",
  DOWN = "down",
  NONE = "none",
}

class ForecastComparisonWidgetCalculationError extends Error {
  debugMessage: string;
  clientErrorMessage: string;
  constructor(debugMessage: string, clientErrorMessage: string) {
    super(debugMessage);
    this.debugMessage = debugMessage;
    this.clientErrorMessage = clientErrorMessage;
  }
}

class PredicitonDoesNotHaveSelectedKpi extends ForecastComparisonWidgetCalculationError {
  constructor(
    predictionData: BudgetMixInput["aggregated_data"]["summary_data"]["kpi_data"],
    selectedKpiName: string,
  ) {
    super(
      `Prediction data ${JSON.stringify(
        predictionData,
      )} does not include kpi ${selectedKpiName}`,
      "Selected KPI not found in forecasted data",
    );
  }
}

class AdoptedMixDoesNotHaveSelectedKpi extends ForecastComparisonWidgetCalculationError {
  constructor(
    adoptedBudgetMixKpiSummary: BudgetMixDatumSummaryData["kpi_data"],
    selectedKpiName: string,
  ) {
    super(
      `Adopted mix data ${JSON.stringify(
        adoptedBudgetMixKpiSummary,
      )} does not include kpi ${selectedKpiName}`,
      "Selected KPI not found in adopted budget",
    );
  }
}

type RangedData = {
  low: number;
  median: number;
  high: number;
};

type KpiPredictionData = {
  kpiValues: RangedData;
  kpiValueChangeDirection: ChangeDirection;
  kpiValueChangeValue: number;
  supplimentalKpiType: SupplimentalKpiType;
  supplimentalKpiValues: RangedData;
  supplimentalKpiValueChangeDirection: ChangeDirection;
  supplimentalKpiValueChangeValue: number;
  totalForecastedBudget: number;
};

type CalculationData = {
  data: KpiPredictionData | null;
  error: string | null;
  shouldRender: boolean;
};

export function ForecastComparisonWidget({
  adoptedBudgetMix,
  proposedBudgetMix,
  selectedKpi,
  isLoading,
  height,
}: ForecastComparisonWidgetProps) {
  const { user } = useUser();
  const { data, error, shouldRender }: CalculationData = useMemo(() => {
    function getRoasOrCacValue(
      kpiValue: number,
      spend: number,
      calculationType: SupplimentalKpiType,
    ) {
      return calculationType === SupplimentalKpiType.ROAS
        ? calculateRoas({
            revenue: kpiValue,
            spend,
          })
        : calculateCac({
            transactions: kpiValue,
            spend,
          });
    }

    if (!proposedBudgetMix || !selectedKpi || !adoptedBudgetMix) {
      return {
        data: null,
        error: null,
        shouldRender: false,
      };
    }
    try {
      const selectedKpiPredictionAggregateValues =
        proposedBudgetMix.aggregated_data.summary_data.kpi_data[
          selectedKpi.kpi_name
        ]?.values;
      if (!selectedKpiPredictionAggregateValues) {
        throw new PredicitonDoesNotHaveSelectedKpi(
          proposedBudgetMix.aggregated_data.summary_data.kpi_data,
          selectedKpi.kpi_name,
        );
      }

      const selectedKpiAdoptedMixAggregateValues =
        adoptedBudgetMix.data.aggregated_data.summary_data.kpi_data[
          selectedKpi.kpi_name
        ]?.values;

      if (!selectedKpiAdoptedMixAggregateValues) {
        throw new AdoptedMixDoesNotHaveSelectedKpi(
          adoptedBudgetMix.data.aggregated_data.summary_data.kpi_data,
          selectedKpi.kpi_name,
        );
      }

      const selectedKpiPredictionAggregate =
        selectedKpiPredictionAggregateValues.median;
      const selectedKpiAdoptedMixAggregate =
        selectedKpiAdoptedMixAggregateValues.median;

      const selectedKpiPredictionChangeDirection =
        selectedKpiPredictionAggregate > selectedKpiAdoptedMixAggregate
          ? ChangeDirection.UP
          : selectedKpiPredictionAggregate < selectedKpiAdoptedMixAggregate
          ? ChangeDirection.DOWN
          : ChangeDirection.NONE;

      const selectedKpiPredictionChangeValue = getFractionChanged({
        current: selectedKpiPredictionAggregate,
        historical: selectedKpiAdoptedMixAggregate,
      });

      const forecastedTotalSpend =
        proposedBudgetMix.aggregated_data.summary_data.spend;
      const adoptedMixTotalSpend =
        adoptedBudgetMix.data.aggregated_data.summary_data.spend;

      const supplimentalKpiType =
        selectedKpi.kpi_type === KpiType.REVENUE
          ? SupplimentalKpiType.ROAS
          : SupplimentalKpiType.CAC;

      const supplimentalKpiPredictedValues = {
        low: getRoasOrCacValue(
          selectedKpiPredictionAggregateValues.low,
          forecastedTotalSpend,
          supplimentalKpiType,
        ),
        median: getRoasOrCacValue(
          selectedKpiPredictionAggregateValues.median,
          forecastedTotalSpend,
          supplimentalKpiType,
        ),
        high: getRoasOrCacValue(
          selectedKpiPredictionAggregateValues.high,
          forecastedTotalSpend,
          supplimentalKpiType,
        ),
      };

      const supplimentalKpiAdoptedMixValues = {
        low: getRoasOrCacValue(
          selectedKpiAdoptedMixAggregateValues.low,
          adoptedMixTotalSpend,
          supplimentalKpiType,
        ),
        median: getRoasOrCacValue(
          selectedKpiAdoptedMixAggregateValues.median,
          adoptedMixTotalSpend,
          supplimentalKpiType,
        ),
        high: getRoasOrCacValue(
          selectedKpiAdoptedMixAggregateValues.high,
          adoptedMixTotalSpend,
          supplimentalKpiType,
        ),
      };

      const supplimentalKpiValueChangeDirection =
        supplimentalKpiPredictedValues.median >
        supplimentalKpiAdoptedMixValues.median
          ? ChangeDirection.UP
          : supplimentalKpiPredictedValues.median <
            supplimentalKpiAdoptedMixValues.median
          ? ChangeDirection.DOWN
          : ChangeDirection.NONE;

      const supplimentalKpiValueChangeValue = getFractionChanged({
        current: supplimentalKpiPredictedValues.median,
        historical: supplimentalKpiAdoptedMixValues.median,
      });

      return {
        error: null,
        shouldRender: true,
        data: {
          kpiValues: selectedKpiPredictionAggregateValues,
          kpiValueChangeDirection: selectedKpiPredictionChangeDirection,
          kpiValueChangeValue: selectedKpiPredictionChangeValue,
          supplimentalKpiType: supplimentalKpiType,
          supplimentalKpiValues: supplimentalKpiPredictedValues,
          supplimentalKpiValueChangeDirection:
            supplimentalKpiValueChangeDirection,
          supplimentalKpiValueChangeValue: supplimentalKpiValueChangeValue,
          totalForecastedBudget: forecastedTotalSpend,
        },
      };
    } catch (err) {
      if (err instanceof ForecastComparisonWidgetCalculationError) {
        return {
          data: null,
          error: err.clientErrorMessage,
          shouldRender: true,
        };
      } else {
        return {
          data: null,
          error: "Unknown error",
          shouldRender: true,
        };
      }
    }
  }, [selectedKpi, adoptedBudgetMix, proposedBudgetMix]);

  const formatKpi = (kpiValue: number, kpiType: KpiType) => {
    if (kpiType === KpiType.REVENUE) {
      return formatMetric.currency(kpiValue, {
        code: user.currencies.currentCurrency,
        precision: "approximate",
      });
    } else {
      return formatMetric.decimal(kpiValue, { precision: "approximate" });
    }
  };

  const formatRoas = (value: number) => {
    return formatPotentiallyInfiniteNumber(
      value,
      (nonInfValue) => `${round(nonInfValue, 1)}x`,
      (infVal) => `${infVal}x`,
    );
  };

  const formatCac = (value: number) => {
    return formatPotentiallyInfiniteNumber(
      value,
      (nonInfValue) =>
        formatCurrencyToDollarAmount(
          nonInfValue,
          user.currencies.currentCurrency,
        ),
      (infValue) =>
        `${getCurrencySymbol(user.currencies.currentCurrency)}${infValue}`,
    );
  };

  const formatSupplimentalMetric = (
    value: number,
    supplimentalKpiType: SupplimentalKpiType,
  ) => {
    return supplimentalKpiType === SupplimentalKpiType.ROAS
      ? formatRoas(value)
      : formatCac(value);
  };

  const getChangeIcon = (
    formattedChangePercentage: string,
    changeDirection: ChangeDirection,
    desiredChangeDirection: ChangeDirection,
  ) => {
    const directionIsDesired = changeDirection === desiredChangeDirection;
    return (
      <div
        className={clsx("flex justify-start gap-1 items-center text-xs", {
          "text-alien":
            directionIsDesired && changeDirection !== ChangeDirection.NONE,
          "text-mars":
            !directionIsDesired && changeDirection !== ChangeDirection.NONE,
          "text-interstellar-1": changeDirection === ChangeDirection.NONE,
        })}
      >
        <i
          className={clsx("fa-regular", {
            "fa-arrow-up": changeDirection === ChangeDirection.UP,
            "fa-arrow-down": changeDirection === ChangeDirection.DOWN,
            "fa-arrow-left-right": changeDirection === ChangeDirection.NONE,
          })}
        ></i>
        <span>{formattedChangePercentage}</span>
      </div>
    );
  };

  const formatPercentage = (fraction: number) => {
    return `${round(fraction * 100, 1)}%`;
  };

  if (!shouldRender || isLoading || !data) {
    return (
      <div style={{ height: `${height}px` }}>
        <LoadingSlide text="Forecasting your custom budgets, this could take a minute or two..." />
      </div>
    );
  }

  return (
    <div
      className="flex flex-col justify-start items-start gap-8 rounded-sm"
      style={{ height }}
    >
      {error && <span className="text-mars">{error}</span>}
      <div className="flex flex-col justify-start items-start gap-4 w-full p-5">
        <div className="flex justify-start items-center gap-4">
          <span className="font-semibold">{selectedKpi.friendly_name}</span>
          {getChangeIcon(
            formatPercentage(Math.abs(data.kpiValueChangeValue)),
            data.kpiValueChangeDirection,
            ChangeDirection.UP,
          )}
        </div>
        <div className="flex justify-between items-center gap-1 text-sm w-full min-w-full max-w-full">
          <span>{formatKpi(data.kpiValues.low, selectedKpi.kpi_type)}</span>
          <div className="w-full border border-interstellar-2"></div>
          <span className="font-semibold">
            {formatKpi(data.kpiValues.median, selectedKpi.kpi_type)}
          </span>
          <div className="w-full border border-interstellar-2"></div>
          <span>{formatKpi(data.kpiValues.high, selectedKpi.kpi_type)}</span>
        </div>
      </div>
      <div className="flex justify-between items-center min-w-full h-full">
        <div className="flex flex-col justify-center items-start gap-8 border-t-3 border-r-3 border-b-3 border-interstellar-2 p-4 pl-8 w-full h-full">
          <div className="flex justify-start items-center gap-4">
            <span className="uppercase font-semibold">
              {data.supplimentalKpiType}
            </span>
            {getChangeIcon(
              formatPercentage(Math.abs(data.supplimentalKpiValueChangeValue)),
              data.supplimentalKpiValueChangeDirection,
              data.supplimentalKpiType === SupplimentalKpiType.ROAS
                ? ChangeDirection.UP
                : ChangeDirection.DOWN,
            )}
          </div>
          <span className="font-semibold text-sm">
            {formatSupplimentalMetric(
              data.supplimentalKpiValues.low,
              data.supplimentalKpiType,
            )}{" "}
            -{" "}
            {formatSupplimentalMetric(
              data.supplimentalKpiValues.high,
              data.supplimentalKpiType,
            )}
          </span>
        </div>
        <div className="w-full border-interstellar-2 border-t-3 border-b-3 h-full p-4 pl-8">
          <div className="w-full h-full flex flex-col justify-center items-start gap-8 rounded-sm border-interstellar-2">
            <div className="font-semibold">Budget</div>
            <div>
              {formatCurrency(data.totalForecastedBudget, {
                code: user.currencies.currentCurrency,
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
